import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConnectionStatus } from '../../types/connections';
import { Pipeline } from '../../types/pipelines';
import { apiFetch } from '../../services/api';
import { PipelineFormLayout } from '../PipelineFormLayout';
import { STANDARD_GA4_PARAMS, STANDARD_GA4_EVENTS } from '../../constants/ga4';

const GA4FlattenerForm: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditing = !!id;

    const [pipelineName, setPipelineName] = useState('');
    const [selectedGBQConnection, setSelectedGBQConnection] = useState<string>('');
    const [selectedGA4Connection, setSelectedGA4Connection] = useState<string>('');
    const [sessionTimeout, setSessionTimeout] = useState<number>(30);
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
    
    // BigQuery State
    const [bqProjects, setBqProjects] = useState<{ id: string; name: string }[]>([]);
    const [selectedProject, setSelectedProject] = useState<string>('');
    const [bqDatasets, setBqDatasets] = useState<string[]>([]);
    const [selectedDataset, setSelectedDataset] = useState<string>('');
    const [loadingBQ, setLoadingBQ] = useState(false);
    const [bqError, setBqError] = useState<string | null>(null);
    
    // GA4 Metadata State
    const [dimensions, setDimensions] = useState<any[]>([]);
    const [metrics, setMetrics] = useState<any[]>([]);
    const [eventParams, setEventParams] = useState<any[]>([]);
    const [events, setEvents] = useState<any[]>([]);
    const [selectedDimensions, setSelectedDimensions] = useState<string[]>([]);
    const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
    const [selectedEventParams, setSelectedEventParams] = useState<string[]>([]);
    const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
    const [loadingMetadata, setLoadingMetadata] = useState(false);
    const [metadataError, setMetadataError] = useState<React.ReactNode | null>(null);
    const [eventParamsError, setEventParamsError] = useState<React.ReactNode | null>(null);
    const [eventsError, setEventsError] = useState<React.ReactNode | null>(null);
    const [showStandardParams, setShowStandardParams] = useState(false);
    const [showStandardEvents, setShowStandardEvents] = useState(false);
    const [hasLoadedDimensions, setHasLoadedDimensions] = useState(false);
    const [hasLoadedMetrics, setHasLoadedMetrics] = useState(false);

    // GA4 Hierarchy State
    const [ga4Accounts, setGa4Accounts] = useState<{ id: string, name: string }[]>([]);
    const [selectedAccount, setSelectedAccount] = useState<string>('');
    const [ga4Properties, setGa4Properties] = useState<{ id: string, name: string }[]>([]);
    const [selectedProperty, setSelectedProperty] = useState<string>('');
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [loadingProperties, setLoadingProperties] = useState(false);
    const [accountError, setAccountError] = useState<React.ReactNode | null>(null);
    const [propertyError, setPropertyError] = useState<string | null>(null);

    useEffect(() => {
        loadConnectionStatus();
        if (isEditing) {
            loadPipeline(id!);
        }
    }, [id, isEditing]);

    const loadConnectionStatus = async () => {
        try {
            const res = await apiFetch('/api/connections/status');
            if (res.ok) {
                setConnectionStatus(await res.json());
            }
        } catch (error) {
            console.error('Failed to load connection status:', error);
        }
    };

    const loadPipeline = async (pipelineId: string) => {
        try {
            const res = await apiFetch(`/api/pipelines/${pipelineId}`);
            if (res.ok) {
                const pipeline: Pipeline = await res.json();
                setPipelineName(pipeline.name);
                if (pipeline.connections?.gbqConnectionId) setSelectedGBQConnection(pipeline.connections.gbqConnectionId);
                if (pipeline.connections?.ga4ConnectionId) setSelectedGA4Connection(pipeline.connections.ga4ConnectionId);
                if (pipeline.parameters?.sessionTimeout) setSessionTimeout(pipeline.parameters.sessionTimeout);
                if (pipeline.dataSources?.ga4_export?.project) setSelectedProject(pipeline.dataSources.ga4_export.project);
                else if (pipeline.parameters?.ga4Project) setSelectedProject(pipeline.parameters.ga4Project);
                if (pipeline.dataSources?.ga4_export?.dataset) setSelectedDataset(pipeline.dataSources.ga4_export.dataset);
                else if (pipeline.parameters?.ga4Dataset) setSelectedDataset(pipeline.parameters.ga4Dataset);
                if (pipeline.parameters?.ga4Dimensions) setSelectedDimensions(pipeline.parameters.ga4Dimensions);
                if (pipeline.parameters?.ga4Metrics) setSelectedMetrics(pipeline.parameters.ga4Metrics);
                if (pipeline.parameters?.ga4EventParams) setSelectedEventParams(pipeline.parameters.ga4EventParams);
                if (pipeline.parameters?.ga4DiscoveredParams) setEventParams(pipeline.parameters.ga4DiscoveredParams);
                if (pipeline.parameters?.ga4DiscoveredDimensions) setDimensions(pipeline.parameters.ga4DiscoveredDimensions);
                if (pipeline.parameters?.ga4DiscoveredMetrics) setMetrics(pipeline.parameters.ga4DiscoveredMetrics);
                if (pipeline.parameters?.ga4Events) setSelectedEvents(pipeline.parameters.ga4Events);
                if (pipeline.parameters?.ga4DiscoveredEvents) setEvents(pipeline.parameters.ga4DiscoveredEvents);
                if (pipeline.dataSources?.ga4?.accountId) setSelectedAccount(pipeline.dataSources.ga4.accountId);
                else if (pipeline.parameters?.ga4AccountId) setSelectedAccount(pipeline.parameters.ga4AccountId);
                if (pipeline.dataSources?.ga4?.propertyId) setSelectedProperty(pipeline.dataSources.ga4.propertyId);
                else if (pipeline.parameters?.ga4PropertyId) setSelectedProperty(pipeline.parameters.ga4PropertyId);
            }
        } catch (error) {
            console.error('Failed to load pipeline:', error);
        }
    };

    const fetchBQProjects = useCallback(async () => {
        if (!selectedGBQConnection) return;
        setLoadingBQ(true);
        setBqError(null);
        try {
            const response = await apiFetch(`/api/connections/bigquery/projects?gbqConnectionId=${selectedGBQConnection}`);
            if (!response.ok) throw new Error('Failed to fetch projects');
            const data = await response.json();
            const projects = Array.isArray(data) ? data.map((p: any) =>
                typeof p === 'string' ? { id: p, name: p } : p
            ) : [];
            setBqProjects(projects);
        } catch (error: any) {
            setBqError(error.message);
        } finally {
            setLoadingBQ(false);
        }
    }, [selectedGBQConnection]);

    const fetchBQDatasets = useCallback(async (projectId: string) => {
        if (!selectedGBQConnection) return;
        setLoadingBQ(true);
        setBqError(null);
        try {
            const response = await apiFetch(`/api/connections/bigquery/datasets?projectId=${projectId}&gbqConnectionId=${selectedGBQConnection}`);
            if (!response.ok) throw new Error('Failed to fetch datasets');
            const data = await response.json();
            const datasets = Array.isArray(data) ? data : [];
            setBqDatasets(datasets);
            
            // Auto-select analytics_{property_id} dataset if property is already selected
            if (selectedProperty && datasets.length > 0) {
                const analyticsDataset = `analytics_${selectedProperty}`;
                if (datasets.includes(analyticsDataset)) {
                    setSelectedDataset(analyticsDataset);
                }
            }
        } catch (error: any) {
            setBqError(error.message);
        } finally {
            setLoadingBQ(false);
        }
    }, [selectedGBQConnection, selectedProperty]);

    const fetchGA4Accounts = useCallback(async () => {
        if (!selectedGA4Connection) return;
        setLoadingAccounts(true);
        setAccountError(null);
        try {
            const response = await apiFetch(`/api/connections/ga4/accounts?ga4ConnectionId=${selectedGA4Connection}`);
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to fetch accounts');
            }
            const data = await response.json();
            setGa4Accounts(Array.isArray(data) ? data : []);
        } catch (error: any) {
            const errorMessage = error.message || '';
            console.error('Error fetching accounts:', errorMessage);

            if (errorMessage.includes('Google Analytics Admin API has not been used') || errorMessage.includes('is disabled')) {
                const projectIdMatch = errorMessage.match(/project (\d+)/);
                const projectId = projectIdMatch ? projectIdMatch[1] : '';
                const link = projectId
                    ? `https://console.developers.google.com/apis/api/analyticsadmin.googleapis.com/overview?project=${projectId}`
                    : `https://console.developers.google.com/apis/api/analyticsadmin.googleapis.com/overview`;

                setAccountError(
                    <span>
                        Error fetching accounts: {errorMessage} <br />
                        Enable it by visiting <a href={link} target="_blank" rel="noopener noreferrer">this link</a> then retry.
                    </span>
                );
            } else {
                setAccountError(errorMessage);
            }
        } finally {
            setLoadingAccounts(false);
        }
    }, [selectedGA4Connection]);

    const fetchGA4Properties = useCallback(async (accountId: string) => {
        if (!selectedGA4Connection) return;
        setLoadingProperties(true);
        setPropertyError(null);
        try {
            const response = await apiFetch(`/api/connections/ga4/properties?ga4ConnectionId=${selectedGA4Connection}&accountId=${accountId}`);
            if (!response.ok) throw new Error('Failed to fetch properties');
            const data = await response.json();
            setGa4Properties(Array.isArray(data) ? data : []);
        } catch (error: any) {
            setPropertyError(error.message);
        } finally {
            setLoadingProperties(false);
        }
    }, [selectedGA4Connection]);

    const fetchGA4Metadata = async (type: 'dimensions' | 'metrics') => {
        if (!selectedGA4Connection || !selectedProperty) {
            setMetadataError('Please select a GA4 Property first');
            return;
        }

        setLoadingMetadata(true);
        setMetadataError(null);
        try {
            const endpoint = type === 'dimensions' ? 'custom_dimensions' : 'custom_metrics';
            const response = await apiFetch(`/api/connections/ga4/${endpoint}?ga4ConnectionId=${selectedGA4Connection}&propertyId=${selectedProperty}`);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to fetch metadata');
            }
            const data = await response.json();

            const mappedData = data.map((item: any) => ({
                apiName: item.parameterName,
                uiName: item.displayName,
                scope: item.scope,
                description: item.description,
                measurementUnit: item.measurementUnit
            }));

            if (type === 'dimensions') {
                setDimensions(mappedData);
                setHasLoadedDimensions(true);
            } else {
                setMetrics(mappedData);
                setHasLoadedMetrics(true);
            }
        } catch (error: any) {
            const errorMessage = error.message || '';
            if (errorMessage.includes('Google Analytics Admin API has not been used') || errorMessage.includes('is disabled')) {
                const projectIdMatch = errorMessage.match(/project (\d+)/);
                const projectId = projectIdMatch ? projectIdMatch[1] : '';
                const link = projectId
                    ? `https://console.developers.google.com/apis/api/analyticsadmin.googleapis.com/overview?project=${projectId}`
                    : `https://console.developers.google.com/apis/api/analyticsadmin.googleapis.com/overview`;

                setMetadataError(
                    <span>
                        Error fetching GA4 metadata: {errorMessage}. <br />
                        Enable it by visiting <a href={link} target="_blank" rel="noopener noreferrer">this link</a> then retry.
                    </span>
                );
            } else {
                setMetadataError(errorMessage);
            }
        } finally {
            setLoadingMetadata(false);
        }
    };

    const fetchEventParams = useCallback(async () => {
        if (!selectedGBQConnection || !selectedProject || !selectedDataset) {
            setEventParamsError('Please select GBQ Project and Dataset first');
            return;
        }

        setLoadingMetadata(true);
        setEventParamsError(null);

        const query = `
      WITH dims AS (
        SELECT 
            key, 
            COUNTIF(value.string_value IS NOT NULL) as cd,
            COUNTIF(value.int_value IS NOT NULL OR value.float_value IS NOT NULL OR value.double_value IS NOT NULL) as cm,
            count(1) as cnt
        FROM \`${selectedProject}.${selectedDataset}.events_*\` , unnest(event_params)
        WHERE _TABLE_SUFFIX BETWEEN FORMAT_DATE('%Y%m%d', DATE_SUB(CURRENT_DATE(), INTERVAL 5 DAY)) AND FORMAT_DATE('%Y%m%d', CURRENT_DATE())
        group by key
        HAVING cnt > 100
      )
      SELECT 
        key,
        IF(cd>cm,'d','m') as type,
        cnt
      FROM dims
      ORDER BY cnt DESC
    `;

        try {
            const response = await apiFetch('/api/connections/bigquery/query', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    gbqConnectionId: selectedGBQConnection,
                    projectId: selectedProject,
                    datasetId: selectedDataset,
                    query
                })
            });

            if (!response.ok) throw new Error('Failed to fetch event params');
            const rows = await response.json();

            const params = rows.map((row: any) => ({
                apiName: row.f[0].v,
                type: row.f[1].v,
                count: row.f[2].v
            }));

            setEventParams(params);

            const standardFound = params
                .filter((p: any) => STANDARD_GA4_PARAMS.includes(p.apiName))
                .map((p: any) => p.apiName);

            setSelectedEventParams(prev => {
                const newSelection = new Set([...prev, ...standardFound]);
                return Array.from(newSelection);
            });
        } catch (error: any) {
            const msg = error.message || 'Unknown error';
            if (msg.includes('Invalid ID token')) {
                setEventParamsError(
                    <span>
                        Session expired. Please <button className="link-button" onClick={() => window.location.reload()}>refresh the page</button> to log in again.
                    </span>
                );
            } else {
                setEventParamsError(msg);
            }
        } finally {
            setLoadingMetadata(false);
        }
    }, [selectedGBQConnection, selectedProject, selectedDataset]);

    const fetchGA4Events = useCallback(async () => {
        if (!selectedGBQConnection || !selectedProject || !selectedDataset) {
            setEventsError('Please select GBQ Project and Dataset first');
            return;
        }

        setLoadingMetadata(true);
        setEventsError(null);

        const query = `
          SELECT 
           event_name, 
           count(1) as cnt
          FROM  
          \`${selectedProject}.${selectedDataset}.events_*\` 
          WHERE _TABLE_SUFFIX BETWEEN FORMAT_DATE('%Y%m%d', DATE_SUB(CURRENT_DATE(), INTERVAL 5 DAY)) AND FORMAT_DATE('%Y%m%d', CURRENT_DATE())
          GROUP BY event_name
          HAVING cnt > 100
          ORDER BY cnt DESC
        `;

        try {
            const response = await apiFetch('/api/connections/bigquery/query', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    gbqConnectionId: selectedGBQConnection,
                    projectId: selectedProject,
                    datasetId: selectedDataset,
                    query
                })
            });

            if (!response.ok) throw new Error('Failed to fetch events');
            const rows = await response.json();

            const eventList = rows.map((row: any) => ({
                eventName: row.f[0].v,
                count: row.f[1].v
            }));

            setEvents(eventList);

            const standardFound = eventList
                .filter((e: any) => STANDARD_GA4_EVENTS.includes(e.eventName))
                .map((e: any) => e.eventName);

            setSelectedEvents(prev => {
                const newSelection = new Set([...prev, ...standardFound]);
                return Array.from(newSelection);
            });

        } catch (error: any) {
            const msg = error.message || 'Unknown error';
            if (msg.includes('Invalid ID token')) {
                setEventsError(
                    <span>
                        Session expired. Please <button className="link-button" onClick={() => window.location.reload()}>refresh the page</button> to log in again.
                    </span>
                );
            } else {
                setEventsError(msg);
            }
        } finally {
            setLoadingMetadata(false);
        }
    }, [selectedGBQConnection, selectedProject, selectedDataset]);

    useEffect(() => {
        if (selectedGBQConnection) {
            fetchBQProjects();
        }
    }, [selectedGBQConnection, fetchBQProjects]);

    useEffect(() => {
        if (selectedProject && selectedGBQConnection) {
            setSelectedDataset('');
            fetchBQDatasets(selectedProject);
        }
    }, [selectedProject, selectedGBQConnection, fetchBQDatasets]);

    useEffect(() => {
        if (selectedProperty && bqDatasets.length > 0 && selectedProject) {
            const analyticsDataset = `analytics_${selectedProperty}`;
            if (bqDatasets.includes(analyticsDataset) && selectedDataset !== analyticsDataset) {
                setSelectedDataset(analyticsDataset);
            }
        }
    }, [selectedProperty, bqDatasets, selectedProject, selectedDataset]);

    useEffect(() => {
        if (selectedGA4Connection) {
            fetchGA4Accounts();
        } else {
            setGa4Accounts([]);
            setSelectedAccount('');
            setGa4Properties([]);
            setSelectedProperty('');
        }
    }, [selectedGA4Connection, fetchGA4Accounts]);

    useEffect(() => {
        if (selectedAccount) {
            fetchGA4Properties(selectedAccount);
        } else {
            setGa4Properties([]);
            setSelectedProperty('');
            setHasLoadedDimensions(false);
            setHasLoadedMetrics(false);
        }
    }, [selectedAccount, fetchGA4Properties]);

    useEffect(() => {
        if (selectedGBQConnection && selectedProject && selectedDataset && events.length === 0) {
            fetchGA4Events();
        }
    }, [selectedGBQConnection, selectedProject, selectedDataset, fetchGA4Events, events.length]);

    useEffect(() => {
        if (selectedGBQConnection && selectedProject && selectedDataset && eventParams.length === 0) {
            fetchEventParams();
        }
    }, [selectedGBQConnection, selectedProject, selectedDataset, fetchEventParams, eventParams.length]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const pipelineData = {
            type: 'ga4-flattener' as const,
            name: pipelineName || 'GA4 Flattener Pipeline',
            connections: {
                gbqConnectionId: selectedGBQConnection,
                ga4ConnectionId: selectedGA4Connection
            },
            dataSources: {
                ga4: {
                    accountId: selectedAccount,
                    propertyId: selectedProperty
                },
                ga4_export: {
                    project: selectedProject,
                    dataset: selectedDataset
                }
            },
            parameters: {
                sessionTimeout,
                ga4Dimensions: selectedDimensions,
                ga4Metrics: selectedMetrics,
                ga4EventParams: selectedEventParams,
                ga4Events: selectedEvents,
                ga4DiscoveredParams: eventParams,
                ga4DiscoveredDimensions: dimensions,
                ga4DiscoveredMetrics: metrics,
                ga4DiscoveredEvents: events
            }
        };

        try {
            const method = isEditing ? 'PUT' : 'POST';
            const url = isEditing ? `/api/pipelines/${id}` : '/api/pipelines';

            const res = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(pipelineData)
            });

            if (res.ok) {
                navigate('/pipelines');
            } else {
                throw new Error('Failed to save pipeline');
            }
        } catch (error) {
            console.error('Error saving pipeline:', error);
            alert('Failed to save pipeline');
        }
    };

    const toggleSelection = (list: string[], setList: (l: string[]) => void, item: string) => {
        if (list.includes(item)) {
            setList(list.filter(i => i !== item));
        } else {
            setList([...list, item]);
        }
    };

    return (
        <PipelineFormLayout isEditing={isEditing} pipelineType="ga4-flattener">
            <form onSubmit={handleSubmit}>
                {/* GA4 Connector Block */}
                <div className="form-section">
                    <h3>GA4 Connector (optional)</h3>
                    <p style={{ color: '#64748b', fontSize: '14px', marginTop: '-16px', marginBottom: '24px', paddingLeft: '16px' }}>
                        Configure your Google Analytics 4 connection, account, and property
                    </p>
                    <div className="form-group">
                        <label>GA4 Connection</label>
                        <select
                            value={selectedGA4Connection}
                            onChange={e => setSelectedGA4Connection(e.target.value)}
                        >
                            <option value="">Select Connection</option>
                            {connectionStatus?.ga4?.map(c => (
                                <option key={c.id} value={c.id}>{c.email}</option>
                            ))}
                        </select>
                    </div>
                    {selectedGA4Connection && (
                        <>
                            <div className="form-group">
                                <label>GA4 Account</label>
                                <select
                                    value={selectedAccount}
                                    onChange={e => setSelectedAccount(e.target.value)}
                                    disabled={loadingAccounts}
                                >
                                    <option value="">{loadingAccounts ? 'Loading...' : 'Select Account'}</option>
                                    {ga4Accounts.map(a => (
                                        <option key={a.id} value={a.id}>{a.name}</option>
                                    ))}
                                </select>
                                {accountError && <div className="error-message">{accountError}</div>}
                            </div>
                            <div className="form-group">
                                <label>GA4 Property</label>
                                <select
                                    value={selectedProperty}
                                    onChange={e => {
                                        setSelectedProperty(e.target.value);
                                        setHasLoadedDimensions(false);
                                        setHasLoadedMetrics(false);
                                        setDimensions([]);
                                        setMetrics([]);
                                    }}
                                    disabled={loadingProperties || !selectedAccount}
                                >
                                    <option value="">{loadingProperties ? 'Loading...' : 'Select Property'}</option>
                                    {ga4Properties.map(p => (
                                        <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                                    ))}
                                </select>
                                {propertyError && <div className="error-message">{propertyError}</div>}
                            </div>
                        </>
                    )}
                </div>

                {/* GA4 Custom Configuration */}
                {selectedGA4Connection && selectedProperty && (
                    <div className="form-section">
                        <h3>GA4 Custom Configuration (optional)</h3>
                        <p style={{ color: '#64748b', fontSize: '14px', marginTop: '-16px', marginBottom: '24px', paddingLeft: '16px' }}>
                            Select custom dimensions and metrics from your GA4 property
                        </p>
                        {metadataError && <div className="error-message">{metadataError}</div>}

                        <div className="metadata-section">
                            <h4>GA4 custom dimensions</h4>
                            <button type="button" onClick={() => fetchGA4Metadata('dimensions')} disabled={loadingMetadata}>
                                Get Custom Dimensions
                            </button>
                            {hasLoadedDimensions && !loadingMetadata && dimensions.length === 0 && (
                                <div className="info-text" style={{ marginTop: '12px' }}>
                                    No custom dimensions found. No custom dimensions have been created in this property.
                                </div>
                            )}
                            {dimensions.length > 0 && (
                                <div className="table-container">
                                    <table className="definition-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '40px' }}>Select</th>
                                                <th>Dimension name</th>
                                                <th>Description</th>
                                                <th>Scope</th>
                                                <th>User Property/Parameter</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dimensions.map(d => (
                                                <tr key={d.apiName}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedDimensions.includes(d.apiName)}
                                                            onChange={() => toggleSelection(selectedDimensions, setSelectedDimensions, d.apiName)}
                                                        />
                                                    </td>
                                                    <td>{d.uiName || d.apiName}</td>
                                                    <td>{d.description}</td>
                                                    <td>{d.scope}</td>
                                                    <td>{d.apiName}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div className="metadata-section">
                            <h4>GA4 custom metrics</h4>
                            <button type="button" onClick={() => fetchGA4Metadata('metrics')} disabled={loadingMetadata}>
                                Get Custom Metrics
                            </button>
                            {hasLoadedMetrics && !loadingMetadata && metrics.length === 0 && (
                                <div className="info-text" style={{ marginTop: '12px' }}>
                                    No custom metrics found. No custom metrics have been created in this property.
                                </div>
                            )}
                            {metrics.length > 0 && (
                                <div className="table-container">
                                    <table className="definition-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '40px' }}>Select</th>
                                                <th>Metric name</th>
                                                <th>Description</th>
                                                <th>Scope</th>
                                                <th>Parameter</th>
                                                <th>Unit</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {metrics.map(m => (
                                                <tr key={m.apiName}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedMetrics.includes(m.apiName)}
                                                            onChange={() => toggleSelection(selectedMetrics, setSelectedMetrics, m.apiName)}
                                                        />
                                                    </td>
                                                    <td>{m.uiName || m.apiName}</td>
                                                    <td>{m.description}</td>
                                                    <td>{m.scope}</td>
                                                    <td>{m.apiName}</td>
                                                    <td>{m.measurementUnit}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* BigQuery Connector Block */}
                <div className="form-section">
                    <h3>BigQuery Connector</h3>
                    <div className="form-group">
                        <label>BigQuery Connection</label>
                        <select
                            value={selectedGBQConnection}
                            onChange={e => setSelectedGBQConnection(e.target.value)}
                            required
                        >
                            <option value="">Select Connection</option>
                            {connectionStatus?.gbq?.map(c => (
                                <option key={c.id} value={c.id}>{c.email}</option>
                            ))}
                        </select>
                    </div>
                    {selectedGBQConnection && (
                        <>
                            {bqError && <div className="error-message">{bqError}</div>}
                            <div className="form-group">
                                <label className="required">Project</label>
                                <select
                                    value={selectedProject}
                                    onChange={e => {
                                        setSelectedProject(e.target.value);
                                        setSelectedDataset('');
                                        if (!pipelineName && e.target.value && bqProjects.length > 0) {
                                            const project = bqProjects.find(p => p.id === e.target.value);
                                            if (project) {
                                                setPipelineName(project.name);
                                            }
                                        }
                                    }}
                                    required
                                    disabled={loadingBQ}
                                >
                                    <option value="">{loadingBQ ? 'Loading...' : 'Select Project'}</option>
                                    {bqProjects.map(p => (
                                        <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="required">Dataset</label>
                                <select
                                    value={selectedDataset}
                                    onChange={e => setSelectedDataset(e.target.value)}
                                    required
                                    disabled={loadingBQ || !selectedProject}
                                >
                                    <option value="">{loadingBQ ? 'Loading...' : 'Select Dataset'}</option>
                                    {bqDatasets.map(d => {
                                        const isAnalyticsDataset = selectedProperty && d === `analytics_${selectedProperty}`;
                                        return (
                                            <option
                                                key={d}
                                                value={d}
                                                style={isAnalyticsDataset ? { fontWeight: 'bold' } : undefined}
                                            >
                                                {d}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </>
                    )}
                </div>

                {/* GA4 raw export Autodiscovery */}
                {selectedGBQConnection && selectedDataset && (
                    <div className="form-section">
                        <h3>GA4 raw export Autodiscovery</h3>
                        <p style={{ color: '#64748b', fontSize: '14px', marginTop: '-16px', marginBottom: '24px', paddingLeft: '16px' }}>
                            Discover and select events and event parameters from your GA4 export dataset
                        </p>
                        <div className="metadata-section">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <h4 style={{ margin: 0 }}>Events</h4>
                                {events.length > 0 && (
                                    <button
                                        type="button"
                                        className="link-button"
                                        onClick={fetchGA4Events}
                                        disabled={loadingMetadata}
                                        style={{ fontSize: '13px', fontWeight: 'normal' }}
                                    >
                                        Refresh
                                    </button>
                                )}
                            </div>
                            {loadingMetadata && events.length === 0 && (
                                <div style={{ color: '#64748b', fontSize: '14px', marginBottom: '16px' }}>Loading events...</div>
                            )}
                            {eventsError && <div className="error-message">{eventsError}</div>}
                            {events.length > 0 && (
                                <>
                                    {(() => {
                                        const standardEvents = events.filter(e => STANDARD_GA4_EVENTS.includes(e.eventName));
                                        const customEvents = events.filter(e => !STANDARD_GA4_EVENTS.includes(e.eventName));

                                        return (
                                            <>
                                                {standardEvents.length > 0 && (
                                                    <div style={{ marginBottom: '24px' }}>
                                                        <h5
                                                            style={{
                                                                margin: 0,
                                                                marginBottom: showStandardEvents ? '12px' : '0',
                                                                cursor: 'pointer',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                color: '#475569',
                                                                fontSize: '14px',
                                                                fontWeight: '500'
                                                            }}
                                                            onClick={() => setShowStandardEvents(!showStandardEvents)}
                                                        >
                                                            <span style={{ fontSize: '12px', transition: 'transform 0.2s', transform: showStandardEvents ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                                                            Standard Events (Required) ({standardEvents.length})
                                                        </h5>
                                                        {showStandardEvents && (
                                                            <div className="compact-list" style={{ marginTop: '12px' }}>
                                                                <div className="compact-list-header">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={standardEvents.length > 0 && standardEvents.every(e => selectedEvents.includes(e.eventName))}
                                                                        onChange={(e) => {
                                                                            const isChecked = e.target.checked;
                                                                            const standardEventNames = standardEvents.map(e => e.eventName);

                                                                            setSelectedEvents(prev => {
                                                                                if (isChecked) {
                                                                                    const newSet = new Set([...prev, ...standardEventNames]);
                                                                                    return Array.from(newSet);
                                                                                } else {
                                                                                    return prev.filter(e => !standardEventNames.includes(e));
                                                                                }
                                                                            });
                                                                        }}
                                                                    />
                                                                    <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>Select All</span>
                                                                </div>
                                                                <div className="compact-list-items">
                                                                    {standardEvents.map(e => (
                                                                        <label key={e.eventName} className="compact-list-item">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selectedEvents.includes(e.eventName)}
                                                                                onChange={() => toggleSelection(selectedEvents, setSelectedEvents, e.eventName)}
                                                                            />
                                                                            <span>{e.eventName} <span className="count-badge">({e.count})</span></span>
                                                                        </label>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {customEvents.length > 0 && (
                                                    <div>
                                                        <h5 style={{ margin: 0, marginBottom: '12px', color: '#475569', fontSize: '14px', fontWeight: '500' }}>Custom Events ({customEvents.length})</h5>
                                                        <div className="compact-list">
                                                            <div className="compact-list-header">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={customEvents.length > 0 && customEvents.every(e => selectedEvents.includes(e.eventName))}
                                                                    onChange={(e) => {
                                                                        const isChecked = e.target.checked;
                                                                        const customEventNames = customEvents.map(e => e.eventName);

                                                                        setSelectedEvents(prev => {
                                                                            if (isChecked) {
                                                                                const newSet = new Set([...prev, ...customEventNames]);
                                                                                return Array.from(newSet);
                                                                            } else {
                                                                                return prev.filter(e => !customEventNames.includes(e));
                                                                            }
                                                                        });
                                                                    }}
                                                                />
                                                                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>Select All</span>
                                                            </div>
                                                            <div className="compact-list-items">
                                                                {customEvents.map(e => (
                                                                    <label key={e.eventName} className="compact-list-item">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selectedEvents.includes(e.eventName)}
                                                                            onChange={() => toggleSelection(selectedEvents, setSelectedEvents, e.eventName)}
                                                                        />
                                                                        <span>{e.eventName} <span className="count-badge">({e.count})</span></span>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}
                                </>
                            )}
                        </div>

                        <div className="metadata-section">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <h4 style={{ margin: 0 }}>Event Parameters</h4>
                                {eventParams.length > 0 && (
                                    <button
                                        type="button"
                                        className="link-button"
                                        onClick={fetchEventParams}
                                        disabled={loadingMetadata}
                                        style={{ fontSize: '13px', fontWeight: 'normal' }}
                                    >
                                        Refresh
                                    </button>
                                )}
                            </div>
                            {loadingMetadata && eventParams.length === 0 && (
                                <div style={{ color: '#64748b', fontSize: '14px', marginBottom: '16px' }}>Loading event parameters...</div>
                            )}
                            {eventParamsError && <div className="error-message">{eventParamsError}</div>}
                            {eventParams.length > 0 && (
                                <>
                                    {(() => {
                                        const standardParams = eventParams.filter(p => STANDARD_GA4_PARAMS.includes(p.apiName));
                                        const customParams = eventParams.filter(p => !STANDARD_GA4_PARAMS.includes(p.apiName));

                                        return (
                                            <>
                                                {standardParams.length > 0 && (
                                                    <div style={{ marginBottom: '24px' }}>
                                                        <h5
                                                            style={{
                                                                margin: 0,
                                                                marginBottom: showStandardParams ? '12px' : '0',
                                                                cursor: 'pointer',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                color: '#475569',
                                                                fontSize: '14px',
                                                                fontWeight: '500'
                                                            }}
                                                            onClick={() => setShowStandardParams(!showStandardParams)}
                                                        >
                                                            <span style={{ fontSize: '12px', transition: 'transform 0.2s', transform: showStandardParams ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                                                            Standard Parameters (Required) ({standardParams.length})
                                                        </h5>
                                                        {showStandardParams && (
                                                            <div className="compact-list" style={{ marginTop: '12px' }}>
                                                                <div className="compact-list-items">
                                                                    {standardParams.map(p => (
                                                                        <label key={p.apiName} className="compact-list-item">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={true}
                                                                                disabled={true}
                                                                            />
                                                                            <span>{p.apiName} <span className="count-badge">({p.count})</span></span>
                                                                        </label>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {customParams.length > 0 && (
                                                    <div>
                                                        <h5 style={{ margin: 0, marginBottom: '12px', color: '#475569', fontSize: '14px', fontWeight: '500' }}>Custom Parameters ({customParams.length})</h5>
                                                        <div className="compact-list">
                                                            <div className="compact-list-header">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={customParams.length > 0 && customParams.every(p => selectedEventParams.includes(p.apiName))}
                                                                    onChange={(e) => {
                                                                        const isChecked = e.target.checked;
                                                                        const customParamNames = customParams.map(p => p.apiName);

                                                                        setSelectedEventParams(prev => {
                                                                            if (isChecked) {
                                                                                const newSet = new Set([...prev, ...customParamNames]);
                                                                                return Array.from(newSet);
                                                                            } else {
                                                                                return prev.filter(p => !customParamNames.includes(p));
                                                                            }
                                                                        });
                                                                    }}
                                                                />
                                                                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>Select All</span>
                                                            </div>
                                                            <div className="compact-list-items">
                                                                {customParams.map(p => (
                                                                    <label key={p.apiName} className="compact-list-item">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selectedEventParams.includes(p.apiName)}
                                                                            onChange={() => toggleSelection(selectedEventParams, setSelectedEventParams, p.apiName)}
                                                                        />
                                                                        <span>{p.apiName} <span className="count-badge">({p.count})</span></span>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}
                                </>
                            )}
                        </div>
                    </div>
                )}

                <div className="form-section">
                    <div className="form-group">
                        <label>Session timeout, minutes</label>
                        <input
                            type="number"
                            value={sessionTimeout}
                            onChange={e => setSessionTimeout(parseInt(e.target.value) || 30)}
                            min="1"
                            placeholder="30"
                        />
                    </div>
                    <div className="form-group">
                        <label>Name</label>
                        <input
                            type="text"
                            value={pipelineName}
                            onChange={e => setPipelineName(e.target.value)}
                            placeholder="Pipeline Name"
                        />
                    </div>
                </div>

                <div className="form-actions">
                    <button type="submit" className="primary-button">Save Pipeline</button>
                </div>
            </form>
        </PipelineFormLayout>
    );
};

export default GA4FlattenerForm;
